import React, {
  type MouseEvent,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useDaFormContext } from '../../../features/FormPreview/context';
import testIds from '../../constants/testIds';
import { containsAllowedHtmlTags, sanitizeLabelHtml } from '../../utils';
import CommonAsterisk from '../CommonAsterisk/CommonAsterisk';
import ExternalLinkWarningModal from '../ExternalLinkWarningModal/ExternalLinkWarningModal';
import InfoTooltip from '../InfoIcons/InfoTooltip/InfoTooltip';

import './FieldLabel.css';

export interface FieldLabelProps {
  label?: string;
  htmlFor?: string;
  infoTooltipText?: string;
  isCritical?: boolean;
  isRequired?: boolean;
  isReadOnly: boolean;
  shouldRequiredAsterisk?: boolean;
  className?: string;
}

/**
 * FieldLabel component that conditionally supports HTML rendering
 *
 * HTML rendering behavior:
 * - Enabled only for Dynamic Attribute (DA) forms via DaFormContext
 * - When enabled: Renders whitelisted HTML tags in labels
 * - When disabled: Renders labels as plain text
 * - Outside MetadataForm: Defaults to plain text (safe)
 *
 * Supported HTML tags (when enabled):
 * h1-h6, p, b, a, br, em, span, font, i, u, div, strong, li, ul, ol,
 * table, caption, thead, tbody, tfoot, tr, th, td, colgroup, col
 */
function FieldLabel({
  label,
  htmlFor,
  infoTooltipText,
  isCritical = false,
  isRequired = false,
  isReadOnly,
  shouldRequiredAsterisk = false,
  className,
}: FieldLabelProps): ReactElement | null {
  // Get HTML labels flag from context (defaults to false if no provider)
  const { enableHtmlLabels } = useDaFormContext();

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const pendingUrl = useRef<string | null>(null);

  // Check if label contains HTML tags - only when HTML labels are enabled
  const hasHtmlTags = useMemo(
    () => (enableHtmlLabels && label ? containsAllowedHtmlTags(label) : false),
    [enableHtmlLabels, label]
  );

  // Sanitize HTML (preserves nested allowed tags)
  const sanitizedHtml = useMemo(
    () => (hasHtmlTags && label ? sanitizeLabelHtml(label) : null),
    [hasHtmlTags, label]
  );

  // Intercept clicks on <a> tags inside the rendered HTML label
  const handleHtmlClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');

    if (anchor?.href) {
      e.preventDefault();
      pendingUrl.current = anchor.href;
      setIsLinkModalOpen(true);
    }
  }, []);

  const handleLinkContinue = useCallback(() => {
    if (pendingUrl.current) {
      window.open(pendingUrl.current, '_blank', 'noopener,noreferrer');
    }

    pendingUrl.current = null;
    setIsLinkModalOpen(false);
  }, []);

  const handleLinkCancel = useCallback(() => {
    pendingUrl.current = null;
    setIsLinkModalOpen(false);
  }, []);

  if (!label && !infoTooltipText) {
    return null;
  }

  return (
    <Box className='FF-InfoTooltip-labelWrapper'>
      <CommonAsterisk
        isCritical={isCritical}
        isRequired={isRequired}
        shouldRequiredAsterisk={shouldRequiredAsterisk}
      />
      {label && (
        <Typography
          kind='label'
          desktop='h5-d'
          mobile='h5-m'
          htmlFor={htmlFor}
          className={cx(
            'FF-FieldLabel',
            isReadOnly && 'FF-FieldLabel--isReadOnly',
            className
          )}>
          {hasHtmlTags && sanitizedHtml ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              className='FF-FieldLabel-html'
              onClick={handleHtmlClick}
              // eslint-disable-next-line react/no-danger -- Sanitized via DOMPurify
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            label
          )}
        </Typography>
      )}
      {infoTooltipText && <InfoTooltip infoTooltipText={infoTooltipText} />}
      <ExternalLinkWarningModal
        isOpen={isLinkModalOpen}
        onClose={handleLinkCancel}
        onContinue={handleLinkContinue}
        testIds={{
          modal: testIds.FF_DA_LABEL_EXTERNAL_LINK_MODAL,
          continueButton: testIds.FF_DA_LABEL_EXTERNAL_LINK_CONTINUE_BUTTON,
          cancelButton: testIds.FF_DA_LABEL_EXTERNAL_LINK_CANCEL_BUTTON,
        }}
      />
    </Box>
  );
}

export default FieldLabel;
